/**
 * Download file from URL
 * For external URLs (e.g., Cloudflare R2, S3), this uses direct download to avoid CORS issues.
 * For same-origin URLs, this fetches the file first (allows for better error handling).
 */
export async function downloadFile(url: string, filename?: string): Promise<void> {
  if (typeof window === "undefined") {
    throw new Error("Download only supported in browser environment");
  }

  try {
    const urlObj = new URL(url, window.location.href);
    const isExternalUrl = urlObj.origin !== window.location.origin;

    if (isExternalUrl) {
      downloadFileDirect(url, filename);
      return;
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.statusText}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const finalFilename = filename || extractFilenameFromUrl(url);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = finalFilename;
    a.style.display = "none";

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Download failed:", error);
    throw new Error("Failed to download file");
  }
}

function downloadFileDirect(url: string, filename?: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || extractFilenameFromUrl(url);
  a.target = "_blank";
  a.style.display = "none";

  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Download file from Blob object (for API responses that return Blob directly) use case exmple: "/download-contract api"
 * NOTE: Team, This is a BROWSER-ONLY implementation, make a similar function for React Native/Expo when needed. it will use expo-file-system and expo-sharing accordingly..
 */
export function downloadFileFromBlob(blob: Blob, filename: string): void {
  if (typeof window === "undefined") {
    throw new Error("Download only supported in browser environment");
  }

  try {
    const blobUrl = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.style.display = "none";

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Download failed:", error);
    throw new Error("Failed to download file from blob");
  }
}

function extractFilenameFromUrl(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    return pathname.split("/").pop() || "download";
  } catch {
    return "download";
  }
}

